import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  AttachResidentRequest,
  GrantScopeRequest,
  Person,
  Unit,
  VerifyTaxIdRequest,
  VerifyTaxIdResponse,
} from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'

/** Searches start at two characters. */
export const MIN_QUERY = 2

export const unitsQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.units(condoId),
    queryFn: ({ signal }) =>
      api
        .get<Unit[]>('/condominiums/:id/units', {
          params: { id: condoId },
          query: { include: 'residents' },
          signal,
        })
        .then((d) => d ?? []),
  })

export const peopleQuery = (condoId: number, q: string) =>
  queryOptions({
    queryKey: queryKeys.people(condoId, q),
    queryFn: ({ signal }) =>
      api
        .get<Person[]>('/condominiums/:id/people', {
          params: { id: condoId },
          query: { q },
          signal,
        })
        .then((d) => d ?? []),
    enabled: q.length >= MIN_QUERY,
  })

/** Server-side CpfGate: the browser only ever sends the last four digits and gets a verdict back. */
export function useTaxIdCheck(condoId: number, personId: number, last4: string) {
  return useQuery({
    queryKey: ['verify-tax-id', condoId, personId, last4],
    queryFn: ({ signal }) =>
      api.post<VerifyTaxIdResponse>(
        '/condominiums/:id/people/:userId/verify-tax-id',
        { last4 } satisfies VerifyTaxIdRequest,
        { params: { id: condoId, userId: personId }, signal },
      ),
    enabled: /^\d{4}$/.test(last4),
    retry: false,
    gcTime: 0,
    staleTime: 0,
  })
}

interface AttachArgs {
  unitId: number
  person: Person
  scope: 'resident' | 'reviewer' | 'admin'
}

/** Attaching also grants `resident`; Revisador/Administrador need a second call on `…/scopes`. */
export function useAttachResident(condoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ unitId, person, scope }: AttachArgs) => {
      await api.post(
        '/condominiums/:id/units/:unitId/residents',
        { user_id: person.id } satisfies AttachResidentRequest,
        { params: { id: condoId, unitId } },
      )
      if (scope !== 'resident') {
        await api.post(
          '/condominiums/:id/scopes',
          { user_id: person.id, scope } satisfies GrantScopeRequest,
          { params: { id: condoId } },
        )
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.units(condoId) })
      void qc.invalidateQueries({ queryKey: ['people', condoId] })
      void qc.invalidateQueries({ queryKey: queryKeys.team(condoId) })
    },
  })
}

export function useDetachResident(condoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ unitId, userId }: { unitId: number; userId: number }) =>
      api.delete('/condominiums/:id/units/:unitId/residents/:userId', {
        params: { id: condoId, unitId, userId },
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.units(condoId) })
      void qc.invalidateQueries({ queryKey: ['people', condoId] })
    },
  })
}
